import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Bot, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';

const ProactiveCoach = ({ open, onClose, gameState = null }) => {
  const [advice, setAdvice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const previousStreetRef = useRef(null);
  const lastAnalysisKeyRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // PROACTIVE: Auto-analyze when coach opens OR when street changes
  useEffect(() => {
    if (open && gameState) {
      const currentStreet = gameState.street;
      const handNumber = gameState.hand_number;
      
      // Create unique key for this analysis context
      const analysisKey = `${handNumber}_${currentStreet}`;
      
      // Trigger analysis if this is a NEW context (different hand or different street)
      if (lastAnalysisKeyRef.current !== analysisKey) {
        console.log(`[COACH] New analysis needed: ${lastAnalysisKeyRef.current} → ${analysisKey}`);
        lastAnalysisKeyRef.current = analysisKey;
        previousStreetRef.current = currentStreet;
        analyzeHandAutomatically();
      }
    } else if (!open) {
      // Don't reset analysis key when closing - preserves state for reopening
      setAdvice('');
      setError(null);
    }
  }, [open, gameState?.street, gameState?.hand_number]); // Watch hand number AND street

  const analyzeHandAutomatically = async () => {
    setLoading(true);
    setError(null);
    setAdvice('');

    try {
      // Find human player
      const humanPlayer = gameState?.players?.find(p => p.is_human);
      
      if (!humanPlayer) {
        setError('Waiting for game to start...');
        setLoading(false);
        return;
      }

      // Get player's cards - backend returns hole_cards
      const yourCards = humanPlayer.hole_cards || [];
      
      if (yourCards.length === 0) {
        setError('Waiting for cards to be dealt...');
        setLoading(false);
        return;
      }

      // Calculate betting info
      const toCall = Math.max(0, (gameState.current_bet || 0) - (humanPlayer.current_bet || 0));
      const potOdds = gameState.pot > 0 && toCall > 0 ? 
        Math.round((toCall / (gameState.pot + toCall)) * 100) : 0;

      // Build analysis prompt with street-specific context
      const streetMessages = {
        'preflop': 'This is PREFLOP - you only have your hole cards. Consider your position, stack size, and opening range.',
        'flop': 'FLOP just arrived - 3 community cards revealed. How does this board connect with your hand? Consider your equity and opponents\' likely holdings.',
        'turn': 'TURN card dealt - 4th community card. Your hand strength may have changed. Reassess pot odds and drawing possibilities.',
        'river': 'RIVER - final card. All community cards are out. Make your final decision based on complete hand strength.'
      };

      const prompt = `You are a world-class poker coach. Give DIRECT, ACTIONABLE advice.

CURRENT SITUATION - ${(gameState.street || 'preflop').toUpperCase()}:
━━━━━━━━━━━━━━━━━━━━
${streetMessages[gameState.street || 'preflop']}

Your Cards: ${yourCards.join(' ')}
Board: ${gameState.community_cards?.length > 0 ? gameState.community_cards.join(' ') : 'None yet'}

BETTING SITUATION:
Pot: $${gameState.pot || 0}
To Call: $${toCall}
Your Stack: $${humanPlayer.chips || 0}
${potOdds > 0 ? `Pot Odds: ${potOdds}%` : ''}

Active Opponents: ${gameState.players?.filter(p => !p.is_human && !p.folded).length || 0}

━━━━━━━━━━━━━━━━━━━━

GIVE ME YOUR RECOMMENDATION:
Start with one of these: **FOLD**, **CHECK**, **CALL**, or **RAISE**

Then explain WHY in 2-3 sentences, considering the ${gameState.street || 'preflop'} situation.`;

      console.log('[COACH] Sending analysis request...');

      const response = await fetch(`${API_URL}/api/platform/coach/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: prompt,
          context: JSON.stringify({
            your_cards: yourCards,
            community_cards: gameState.community_cards || [],
            street: gameState.street,
            pot: gameState.pot,
            to_call: toCall,
            your_stack: humanPlayer.chips,
            opponents_active: gameState.players?.filter(p => !p.is_human && !p.folded).length || 0
          })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get advice');
      }

      const data = await response.json();
      const coachAdvice = data.advice || data.response || 'Analysis complete.';
      
      console.log('[COACH] Received advice:', coachAdvice);
      setAdvice(coachAdvice);
      
    } catch (err) {
      console.error('[COACH] Error:', err);
      
      // EMERGENCY FALLBACK: Show basic advice even if API fails
      const fallbackAdvice = generateFallbackAdvice(gameState);
      setAdvice(fallbackAdvice);
      setError(`⚠️ Using offline analysis mode. ${err.message}`);
      
    } finally {
      setLoading(false);
    }
  };

  // EMERGENCY FALLBACK: Generate basic advice when API fails
  const generateFallbackAdvice = (game) => {
    if (!game || !game.players) return "**CHECK** or **FOLD** depending on your hand strength.";
    
    const humanPlayer = game.players.find(p => p.is_human);
    if (!humanPlayer) return "Waiting for game to start...";
    
    const toCall = Math.max(0, (game.current_bet || 0) - (humanPlayer.current_bet || 0));
    const potOdds = game.pot > 0 && toCall > 0 ? (toCall / (game.pot + toCall)) * 100 : 0;
    
    // Basic pre-flop advice
    if (game.street === 'preflop') {
      if (toCall === 0) {
        return `**CHECK** for now. You can see the flop for free. Wait to see how the community cards connect with your hand before committing more chips.`;
      } else if (potOdds > 30) {
        return `**FOLD** - You're facing a large bet (${potOdds.toFixed(0)}% of pot). Without a premium hand, this is too expensive to continue. Save your chips for better spots.`;
      } else {
        return `**CALL** if you have a decent hand. The pot odds are reasonable (${potOdds.toFixed(0)}%). See the flop and reassess based on what comes out.`;
      }
    }
    
    // Post-flop basic advice
    if (toCall === 0) {
      return `**CHECK** - Take the free card and see what develops. If you have a strong hand, consider betting to build the pot. If you're weak, checking keeps you in cheaply.`;
    } else if (toCall > humanPlayer.chips * 0.3) {
      return `**FOLD** unless you have a very strong hand. This bet is ${((toCall/humanPlayer.chips)*100).toFixed(0)}% of your stack. Don't risk it without solid cards.`;
    } else {
      return `**CALL** if you think you're ahead or have a good draw. Pot odds: ${potOdds.toFixed(0)}%. If you have top pair or better, consider raising for value.`;
    }
  };

  if (!open) return null;

  return (
    <div 
      className="fixed right-0 top-20 bottom-0 w-full md:w-[420px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-l-2 border-[#C9A227]/40 shadow-2xl z-40 flex flex-col"
      style={{ animation: 'slideIn 0.2s ease-out' }}
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-[#0F3227] to-[#071A14] border-b border-[#C9A227]/30 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#E1C25B] flex items-center justify-center shadow-lg">
            <Bot className="w-5 h-5 text-[#0B0F14]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Pro Coach
              <Sparkles className="w-4 h-4 text-[#C9A227]" />
            </h2>
            <p className="text-xs text-foreground/60">Instant Analysis</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          data-testid="close-coach-btn"
        >
          <X className="w-5 h-5 text-foreground/60" />
        </button>
      </div>

      {/* Content - Fixed height to prevent jumping */}
      <div className="flex-1 overflow-y-auto p-6 min-h-[400px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-12 h-12 text-[#C9A227] animate-spin mb-4" />
            <p className="text-foreground/70 text-sm font-semibold">Analyzing your hand...</p>
            <p className="text-foreground/50 text-xs mt-2">Reading cards, calculating odds, evaluating position</p>
          </div>
        ) : error ? (
          <div className="space-y-4">
            {/* Show advice even with error (fallback mode) */}
            {advice && (
              <div className="bg-gradient-to-br from-[#0F3227] to-[#071A14] border-2 border-[#C9A227]/40 rounded-xl p-6 shadow-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#0B0F14]" />
                  </div>
                  <h3 className="font-bold text-[#C9A227] text-lg">Quick Advice (Offline Mode)</h3>
                </div>
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="text-foreground/95 leading-relaxed space-y-4 text-base">
                    {advice.split('\n\n').map((paragraph, idx) => {
                      // Check if paragraph starts with a section label like [State Check], [Best Play], etc.
                      const labelMatch = paragraph.match(/^\[([^\]]+)\]\s*(.*)$/s);
                      
                      if (labelMatch) {
                        const [, label, content] = labelMatch;
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="h-1 w-1 rounded-full bg-[#C9A227]"></div>
                              <h4 className="text-[#C9A227] font-semibold text-sm uppercase tracking-wide">
                                {label}
                              </h4>
                            </div>
                            <p className="text-foreground/90 pl-4 whitespace-pre-wrap">{content.trim()}</p>
                          </div>
                        );
                      }
                      
                      // Regular paragraph without label
                      return (
                        <p key={idx} className="whitespace-pre-wrap">{paragraph}</p>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Error notice */}
            <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold mb-1">Limited Analysis Mode</p>
                  <p className="text-foreground/70 text-sm">{error}</p>
                </div>
              </div>
              <button
                onClick={analyzeHandAutomatically}
                className="w-full mt-4 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-semibold transition-colors"
              >
                Retry Full Analysis
              </button>
            </div>
          </div>
        ) : advice ? (
          <div className="space-y-4">
            {/* Game Context Summary */}
            {gameState && (
              <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-2 border-blue-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-400" />
                  <h3 className="font-bold text-sm text-blue-400">
                    {gameState.street === 'preflop' && 'Pre-Flop Analysis'}
                    {gameState.street === 'flop' && 'Flop Analysis (3 Cards)'}
                    {gameState.street === 'turn' && 'Turn Analysis (4 Cards)'}
                    {gameState.street === 'river' && 'River Analysis (Final Card)'}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-foreground/50">Street:</span>
                    <span className="ml-2 text-white font-bold uppercase">{gameState.street || 'PREFLOP'}</span>
                  </div>
                  <div>
                    <span className="text-foreground/50">Pot:</span>
                    <span className="ml-2 text-emerald-400 font-bold">${gameState.pot?.toLocaleString() || 0}</span>
                  </div>
                  <div>
                    <span className="text-foreground/50">To Call:</span>
                    <span className="ml-2 text-amber-400 font-bold">
                      ${Math.max(0, (gameState.current_bet || 0) - (gameState.players?.find(p => p.is_human)?.current_bet || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-foreground/50">Your Stack:</span>
                    <span className="ml-2 text-white font-bold">
                      ${(gameState.players?.find(p => p.is_human)?.chips || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Pro's Recommendation */}
            <div className="bg-gradient-to-br from-[#0F3227] to-[#071A14] border-2 border-[#C9A227]/40 rounded-xl p-6 shadow-xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#0B0F14]" />
                </div>
                <h3 className="font-bold text-[#C9A227] text-lg">What You Should Do</h3>
              </div>
              <div className="prose prose-invert prose-sm max-w-none">
                <div className="text-foreground/95 leading-relaxed space-y-4 text-base">
                  {advice.split('\n\n').map((paragraph, idx) => {
                    // Check if paragraph starts with a section label like [State Check], [Best Play], etc.
                    const labelMatch = paragraph.match(/^\[([^\]]+)\]\s*(.*)$/s);
                    
                    if (labelMatch) {
                      const [, label, content] = labelMatch;
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="h-1 w-1 rounded-full bg-[#C9A227]"></div>
                            <h4 className="text-[#C9A227] font-semibold text-sm uppercase tracking-wide">
                              {label}
                            </h4>
                          </div>
                          <p className="text-foreground/90 pl-4 whitespace-pre-wrap">{content.trim()}</p>
                        </div>
                      );
                    }
                    
                    // Regular paragraph without label
                    return (
                      <p key={idx} className="whitespace-pre-wrap">{paragraph}</p>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Re-analyze button */}
            <button
              onClick={analyzeHandAutomatically}
              className="w-full py-3 bg-gradient-to-r from-[#C9A227] to-[#E1C25B] text-[#0B0F14] rounded-lg font-bold hover:shadow-lg hover:shadow-[#C9A227]/30 transition-all flex items-center justify-center gap-2"
              data-testid="reanalyze-btn"
            >
              <Sparkles className="w-4 h-4" />
              Get Fresh Analysis
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bot className="w-16 h-16 text-foreground/20 mb-4" />
            <p className="text-foreground/60 text-sm">Waiting for game state...</p>
            <p className="text-foreground/40 text-xs mt-2">Start a hand to get advice</p>
          </div>
        )}
      </div>

      {/* Footer Tip */}
      <div className="flex-shrink-0 bg-gradient-to-t from-slate-900 to-transparent border-t border-white/5 p-4">
        <div className="text-center text-xs text-foreground/40">
          <p>💡 Advice updates automatically when flop/turn/river are dealt</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProactiveCoach;
